import * as React from "react";
import { InternalPopover } from "../internal-popover";
import {
  AriaButtonProps,
  AriaPopoverProps,
  useOverlayTrigger,
  usePress,
} from "react-aria";
import {
  OverlayTriggerProps,
  OverlayTriggerState,
  useOverlayTriggerState,
} from "react-stately";
import { StyleProps } from "../../utils/types";
import { cx } from "../../utils/dom";

interface PopoverProps extends OverlayTriggerProps {
  children?: React.ReactNode;
  useNativeClickHandler?: boolean;
}

interface PopoverContextValue {
  triggerRef: React.RefObject<any>;
  overlayState: OverlayTriggerState;
  triggerProps: AriaButtonProps;
  overlayProps: any;
  props?: PopoverProps;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

export function Popover({
  children,
  useNativeClickHandler,
  ...props
}: PopoverProps) {
  const triggerRef = React.useRef(null);
  const state = useOverlayTriggerState(props);
  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: "dialog" },
    state,
    triggerRef
  );

  return (
    <PopoverContext.Provider
      value={{
        triggerProps,
        triggerRef,
        overlayProps,
        overlayState: state,
        props: { useNativeClickHandler },
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

interface PopoverTriggerProps {
  children: React.ReactElement;
}

export function PopoverTrigger({ children }: PopoverTriggerProps) {
  const ctx = React.useContext(PopoverContext);
  if (!ctx) {
    throw "PopoverTrigger must be used within a Popover component";
  }

  const useClickEvents = ctx.props?.useNativeClickHandler;
  const clickProps = usePress(ctx.triggerProps);

  const triggerProps = useClickEvents
    ? clickProps.pressProps
    : ctx.triggerProps;

  return React.cloneElement(children, {
    ref: ctx.triggerRef,
    ["data-is-pressed"]: clickProps.isPressed,
    ...triggerProps,
  });
}

interface PopoverContentProps
  extends StyleProps,
    Omit<AriaPopoverProps, "triggerRef" | "popoverRef"> {
  width?: number;
}

export function PopoverContent({
  children,
  offset = 12,
  placement,
  style,
  className,
  ...props
}: React.PropsWithChildren<PopoverContentProps>) {
  const ctx = React.useContext(PopoverContext);
  if (!ctx) {
    throw "PopoverContent must be used withing a Popover component";
  }

  if (!ctx.overlayState?.isOpen) {
    return null;
  }

  return (
    <InternalPopover
      offset={offset}
      placement={placement}
      state={ctx.overlayState}
      triggerRef={ctx.triggerRef}
      {...props}
    >
      <div
        {...ctx.overlayProps}
        data-radius="md"
        className={cx(className, "p-popover-content")}
        style={{ ...style }}
      >
        {children}
      </div>
    </InternalPopover>
  );
}
